import { makeAutoObservable } from "mobx"
import type { EmbeddedBrowserCertificateError } from "../electronAPIs/browser"
import { isAllowedBrowserUrl } from "./browserUrl"

export type BrowserNativeImage = {
    getSize: () => { width: number; height: number }
    resize: (options: { width?: number; height?: number; quality?: "good" | "better" | "best" }) => BrowserNativeImage
    toDataURL: () => string
}

export type BrowserInputEvent =
    | { type: "mouseDown" | "mouseUp"; x: number; y: number; button: "left"; clickCount: number }
    | { type: "mouseMove"; x: number; y: number }
    | { type: "mouseWheel"; x: number; y: number; deltaX: number; deltaY: number }
    | { type: "keyDown" | "keyUp"; keyCode: string }

export interface BrowserSurface {
    loadURL: (url: string) => Promise<void>
    reload: () => void
    reloadIgnoringCache: () => void
    stop: () => void
    goBack: () => void
    goForward: () => void
    executeJavaScript: (script: string) => Promise<unknown>
    capturePage: () => Promise<BrowserNativeImage>
    sendInputEvent: (event: BrowserInputEvent) => void
    insertText: (text: string) => void
    focus: () => void
    getWebContentsId: () => number
}

interface BrowserWebviewElement extends HTMLElement {
    loadURL(url: string): Promise<void>
    reload(): void
    reloadIgnoringCache(): void
    stop(): void
    goBack(): void
    goForward(): void
    canGoBack(): boolean
    canGoForward(): boolean
    getTitle(): string
    getURL(): string
    executeJavaScript(script: string): Promise<unknown>
    capturePage(): Promise<BrowserNativeImage>
    sendInputEvent(event: BrowserInputEvent): void
    insertText(text: string): void
    getWebContentsId(): number
}

function isBrowserWebviewElement(value: Element | null): value is BrowserWebviewElement {
    return value instanceof HTMLElement && "loadURL" in value && "executeJavaScript" in value && "capturePage" in value && "getWebContentsId" in value
}

function eventValue(event: Event, key: "url" | "errorCode" | "isMainFrame"): unknown {
    if (key in event) return event[key as keyof Event]
    return undefined
}

const MAX_HISTORY = 50

export class EmbeddedBrowserTab {
    url: string
    title: string
    loading = false
    canGoBack = false
    canGoForward = false
    crashed = false
    errorCode: string | null = null
    certificateError: EmbeddedBrowserCertificateError | null = null
    history: Array<{ url: string; title: string }>
    snapshotGeneration = 0
    navigationGeneration = 0
    ownerSessionId: string | null
    agentClaimable: boolean

    private active = false
    private surfaceInteractive = false
    private element: HTMLElement | null = null
    private elementPartition: string | null = null
    private host: HTMLElement | null = null
    private resizeObserver: ResizeObserver | null = null
    private resizeFrame: number | null = null
    private disposed = false

    constructor(
        readonly id: string,
        url: string,
        readonly surfaceKey: number,
        private readonly invalidateRefs: () => void,
        options: { ownerSessionId?: string | null; agentClaimable?: boolean } = {}
    ) {
        this.url = url
        this.title = url === "about:blank" ? "New tab" : url
        this.history = [{ url, title: this.title }]
        this.ownerSessionId = options.ownerSessionId ?? null
        this.agentClaimable = options.agentClaimable === true
        makeAutoObservable(this)
    }

    claimForAgent(sessionId: string): void {
        if (this.ownerSessionId && this.ownerSessionId !== sessionId) throw new Error("browser_tab_not_owned")
        this.ownerSessionId = sessionId
        this.agentClaimable = false
    }

    mount(host: HTMLElement, partition: string): void {
        if (this.disposed) return
        this.host = host
        const element = this.ensureElement(partition)
        if (element.parentElement !== host) {
            element.remove()
            host.append(element)
        }
        this.observeHost(host)
        this.applyActiveState()
        this.syncSize()
        this.queueSizeSync()
    }

    unmount(host: HTMLElement): void {
        if (this.host !== host) return
        this.stopObservingHost()
        this.host = null
        this.element?.remove()
    }

    setActive(active: boolean): void {
        this.active = active
        this.applyActiveState()
        if (active) this.queueSizeSync()
    }

    setSurfaceInteractive(interactive: boolean): void {
        this.surfaceInteractive = interactive
        this.applyActiveState()
    }

    surface(): BrowserSurface | null {
        if (!this.host || !this.element?.isConnected || !isBrowserWebviewElement(this.element)) return null
        return {
            loadURL: (url) => this.loadURL(url),
            reload: () => this.webview().reload(),
            reloadIgnoringCache: () => this.webview().reloadIgnoringCache(),
            stop: () => this.webview().stop(),
            goBack: () => this.webview().goBack(),
            goForward: () => this.webview().goForward(),
            executeJavaScript: (script) => this.webview().executeJavaScript(script),
            capturePage: () => this.webview().capturePage(),
            sendInputEvent: (event) => this.withPreservedHostFocus(() => this.webview().sendInputEvent(event)),
            insertText: (text) => this.withPreservedHostFocus(() => this.webview().insertText(text)),
            focus: () => this.webview().focus(),
            getWebContentsId: () => this.webview().getWebContentsId(),
        }
    }

    guestId(): number | null {
        const surface = this.surface()
        if (!surface) return null
        try {
            return surface.getWebContentsId()
        } catch {
            return null
        }
    }

    private withPreservedHostFocus(operation: () => void): void {
        const previousFocus = !this.active && document.activeElement instanceof HTMLElement ? document.activeElement : null
        operation()
        if (!previousFocus) return
        const restore = () => {
            if (!this.active && this.element === document.activeElement && previousFocus.isConnected) {
                previousFocus.focus({ preventScroll: true })
            }
        }
        restore()
        window.setTimeout(restore, 25)
    }

    beginSnapshot(): number {
        this.snapshotGeneration += 1
        return this.snapshotGeneration
    }

    showCertificateError(warning: EmbeddedBrowserCertificateError): void {
        this.loading = false
        this.errorCode = "certificate_approval_required"
        this.certificateError = warning
        this.title = "Privacy error"
        this.snapshotGeneration += 1
        this.invalidateRefs()
    }

    clearCertificateError(challengeId?: string): void {
        if (challengeId && this.certificateError?.challengeId !== challengeId) return
        this.certificateError = null
        if (this.errorCode === "certificate_approval_required") this.errorCode = null
    }

    dispose(): void {
        if (this.disposed) return
        this.disposed = true
        this.stopObservingHost()
        this.removeElementListeners()
        this.element?.remove()
        this.element = null
        this.host = null
    }

    private ensureElement(partition: string): HTMLElement {
        if (this.element && this.elementPartition === partition) return this.element
        this.removeElementListeners()
        this.element?.remove()
        const element = document.createElement("webview")
        element.setAttribute("partition", partition)
        element.setAttribute("allowpopups", "true")
        element.setAttribute("src", this.url)
        element.style.position = "absolute"
        element.style.inset = "0"
        element.style.margin = "0"
        element.style.border = "0"
        element.style.setProperty("display", "flex", "important")
        this.element = element
        this.elementPartition = partition
        this.addElementListeners(element)
        return element
    }

    private addElementListeners(element: HTMLElement): void {
        element.addEventListener("dom-ready", this.handleDomReady)
        element.addEventListener("did-start-loading", this.handleStarted)
        element.addEventListener("did-navigate", this.handleCommitted)
        element.addEventListener("did-navigate-in-page", this.handleCommitted)
        element.addEventListener("did-stop-loading", this.handleFinished)
        element.addEventListener("did-fail-load", this.handleFailed)
        element.addEventListener("render-process-gone", this.handleCrashed)
    }

    private removeElementListeners(): void {
        const element = this.element
        if (!element) return
        element.removeEventListener("dom-ready", this.handleDomReady)
        element.removeEventListener("did-start-loading", this.handleStarted)
        element.removeEventListener("did-navigate", this.handleCommitted)
        element.removeEventListener("did-navigate-in-page", this.handleCommitted)
        element.removeEventListener("did-stop-loading", this.handleFinished)
        element.removeEventListener("did-fail-load", this.handleFailed)
        element.removeEventListener("render-process-gone", this.handleCrashed)
    }

    private readonly handleDomReady = () => this.queueSizeSync()
    private readonly handleStarted = () => {
        this.loading = true
        this.errorCode = null
        this.certificateError = null
    }
    private readonly handleCommitted = (event: Event) => {
        if (eventValue(event, "isMainFrame") === false) return
        const webview = isBrowserWebviewElement(this.element) ? this.element : null
        const rawUrl = eventValue(event, "url")
        const url = typeof rawUrl === "string" ? rawUrl : (webview?.getURL() ?? this.url)
        if (!isAllowedBrowserUrl(url)) return
        this.url = url
        this.title = webview?.getTitle() || this.title
        this.errorCode = null
        this.certificateError = null
        this.snapshotGeneration += 1
        this.navigationGeneration += 1
        this.invalidateRefs()
        if (this.history[this.history.length - 1]?.url !== url) this.history = [...this.history.slice(-(MAX_HISTORY - 1)), { url, title: this.title }]
    }
    private readonly handleFinished = () => {
        const webview = isBrowserWebviewElement(this.element) ? this.element : null
        this.loading = false
        this.title = webview?.getTitle() || this.title
        this.canGoBack = webview?.canGoBack() ?? false
        this.canGoForward = webview?.canGoForward() ?? false
    }
    private readonly handleFailed = (event: Event) => {
        if (eventValue(event, "isMainFrame") === false) return
        const code = eventValue(event, "errorCode")
        if (code === -3) return
        this.loading = false
        if (this.certificateError) {
            this.errorCode = "certificate_approval_required"
            return
        }
        this.errorCode = typeof code === "number" ? `load_${code}` : "load_failed"
    }
    private readonly handleCrashed = () => {
        this.crashed = true
        this.loading = false
        this.snapshotGeneration += 1
        this.navigationGeneration += 1
        this.invalidateRefs()
    }

    private observeHost(host: HTMLElement): void {
        this.stopObservingHost()
        window.addEventListener("resize", this.syncSize)
        if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(this.syncSize)
            this.resizeObserver.observe(host)
        }
    }

    private stopObservingHost(): void {
        this.resizeObserver?.disconnect()
        this.resizeObserver = null
        window.removeEventListener("resize", this.syncSize)
        if (this.resizeFrame !== null) window.cancelAnimationFrame(this.resizeFrame)
        this.resizeFrame = null
    }

    private readonly queueSizeSync = () => {
        if (this.resizeFrame !== null) window.cancelAnimationFrame(this.resizeFrame)
        this.resizeFrame = window.requestAnimationFrame(() => {
            this.resizeFrame = null
            this.syncSize()
        })
    }

    private readonly syncSize = () => {
        if (!this.host || !this.element) return
        const bounds = this.host.getBoundingClientRect()
        const width = Math.max(0, Math.round(bounds.width))
        const height = Math.max(0, Math.round(bounds.height))
        this.element.style.width = `${width}px`
        this.element.style.minWidth = `${width}px`
        this.element.style.maxWidth = `${width}px`
        this.element.style.height = `${height}px`
        this.element.style.minHeight = `${height}px`
        this.element.style.maxHeight = `${height}px`
    }

    private applyActiveState(): void {
        if (!this.element) return
        this.element.style.visibility = "visible"
        this.element.style.opacity = this.active ? "1" : "0"
        const interactive = this.active && this.surfaceInteractive
        this.element.style.pointerEvents = interactive ? "auto" : "none"
        this.element.tabIndex = interactive ? 0 : -1
        this.element.setAttribute("aria-hidden", String(!this.active))
    }

    private webview(): BrowserWebviewElement {
        if (!isBrowserWebviewElement(this.element) || !this.element.isConnected) throw new Error("browser_surface_unavailable")
        return this.element
    }

    private async loadURL(url: string): Promise<void> {
        const generation = this.snapshotGeneration
        try {
            await this.webview().loadURL(url)
        } catch (error) {
            if (this.snapshotGeneration > generation) return
            throw error
        }
    }
}
